import React, { useMemo, useState, useEffect } from 'react';
import { useFormBuilder } from '../utils/useFormBuilder';
import { updateWorkOrder } from '../api/work-orderAPI';

const WorkOrderStatusModal = ({ show, statusId, workOrder, onClose, onSaved }) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!show || !workOrder) return;
    setFormData({
      failureId: workOrder.failureId || null,
      causeId: workOrder.causeId || null,
      symptom: workOrder.symptom || '',
      notes: workOrder.notes || '',
      startedAt: workOrder.startedAt || '',
      completedAt: workOrder.completedAt || '',
      completionImages: Array.isArray(workOrder.completionImages)
        ? workOrder.completionImages
        : [],
    });
    setErrors({});
  }, [show, workOrder]);

  const fields = useMemo(() => {
    if (!statusId) return [];
    if (statusId === 2) {
      return [
        { field: 'startedAt', label: 'Started At', type: 'datetime', size: 12, required: true },
      ];
    }
    if (statusId === 4) {
      const base = [
        { field: 'startedAt', label: 'Started At', type: 'datetime', size: 6, required: true },
        { field: 'completedAt', label: 'Completed At', type: 'datetime', size: 6, required: true },
      ];
      if ([2, 8].includes(workOrder?.taskTypeId)) {
        base.push(
          { field: 'causeId', label: 'Cause', type: 'select', size: 6, api: 'cause-codes', displayField: 'name' },
          { field: 'failureId', label: 'Failure', type: 'select', size: 6, api: 'failure-codes', displayField: 'name' },
          { field: 'symptom', label: 'Symptom', type: 'textarea', size: 12 }
        );
      }
      base.push(
        { field: 'notes', label: 'Completion Notes', type: 'textarea', size: 12, required: false },
        {
          field: 'completionImages',
          label: 'Completion Images',
          type: 'images',
          size: 12,
          directory: 'workorderimages',
          required: false,
        },
        {
          field: 'workorderFiles',
          label: 'Documents and Files',
          type: 'files',
          size: 12,
          directory: 'workorderfiles',
          required: false,
        }
      );
      return base;
    }
    return [];
  }, [statusId, workOrder]);

  const { renderField } = useFormBuilder(fields, formData, setFormData, errors);

  const handleSave = async () => {
    const newErrors = {};
    fields.forEach(f => {
      if (f.required && !formData[f.field]) {
        newErrors[f.field] = 'Required';
      }
    });
    if (
      statusId === 4 &&
      formData.startedAt &&
      formData.completedAt &&
      new Date(formData.completedAt) <= new Date(formData.startedAt)
    ) {
      newErrors.completedAt = 'The completion time must be after start time';
    }
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    const payload = { statusId };
    if (formData.startedAt) payload.startedAt = new Date(formData.startedAt).toISOString();
    if (formData.completedAt) payload.completedAt = new Date(formData.completedAt).toISOString();
    if (formData.notes) payload.notes = formData.notes;
    if (formData.failureId) payload.failureId = formData.failureId;
    if (formData.causeId) payload.causeId = formData.causeId;
    if (formData.symptom) payload.symptom = formData.symptom;
    if (formData.completionImages) payload.completionImages = formData.completionImages;
    if (formData.workorderFiles) payload.workorderFiles = formData.workorderFiles;

    try {
      await updateWorkOrder(workOrder.id, payload);
      if (onSaved) onSaved(payload);
      onClose();
    } catch (err) {
      console.error(err);
    }
  };

  if (!show) return null;

  return (
    <div className="modal modal-lg modal-backdrop show d-block" tabIndex="-1">
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {statusId === 2 ? 'Start Work Order' : 'Complete Work Order'}
            </h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            <div className="row g-3">{fields.map(renderField)}</div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSave}>
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkOrderStatusModal;

